import { useState, useEffect, useCallback, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import "./ManufactureBoard.scss";
import { notification, DatePicker, Input, Tooltip, Select } from "antd";
import { ReloadOutlined } from '@ant-design/icons';
import { uuidValidation, macValidation, imeiValidation } from "../../../utils/formValidation";
import { v4 as uuidv4 } from "uuid";
import service from "../../../services/Service";
import dayjs from 'dayjs';
import { withLocale } from "../../../l10n";
import Loading from "../../../utils/loading";
import withNavigation from '../../../components/hooks/withNavigation';
import { useGetModelsLookupQuery, useGetVersionsLookupQuery, useGetModemTypesLookupQuery } from '../../../services/lookupApi.js';

const EMPTY_ARRAY = [];

function ManufactureBoard({ lang, navigate, newBoard, ident, editable }) {

  // ===== 1. ESTADOS =====
  const [ui, setUI] = useState({
    loading: false,
  });

  const [form, setForm] = useState({
    ki_id: "",
    uuid: "",
    physical_id: "",
    date: dayjs().format('DD-MM-YYYY'),
    model_uuid: "",
    version_uuid: "",
    modem_imei: "",
    modem_type_uuid: "",
    notes: "",
    state: "Fabricado",
    hardware_version: "",
    usb: false,
    _id: "",
  });

  const [data, setData] = useState({
    raw: null,
    freeModems: [],
    filtered_version_list: [],
  });

  const [errors, setErrors] = useState({
    uuid: "",
    physical_id: "",
  });

  // ===== 2. SELECTORES =====
  const { data: models = EMPTY_ARRAY } = useGetModelsLookupQuery();
  const { data: versions = EMPTY_ARRAY } = useGetVersionsLookupQuery();
  const { data: modemTypes = EMPTY_ARRAY } = useGetModemTypesLookupQuery();
  const [searchParams] = useSearchParams();

  // ===== 3. HELPERS =====
  const updateUI = (field, value) => setUI(prev => ({ ...prev, [field]: value }));
  const updateForm = (field, value) => setForm(prev => ({ ...prev, [field]: value }));
  const updateData = (field, value) => setData(prev => ({ ...prev, [field]: value }));
  const updateErrors = (field, value) => setErrors(prev => ({ ...prev, [field]: value }));

  // Tipos teltonika que admiten al menos un modelo de box
  const boxCapableTypes = useMemo(
    () => new Set(
      modemTypes
        .filter(type => (type.models ?? []).some(m => m.device_type === 'kibox'))
        .map(type => type.uuid)
    ),
    [modemTypes]
  );

  // Tipos compatibles con el modelo elegido.
  const typesForModel = useMemo(() => {
    if (!form.model_uuid) return null;
    return new Set(
      modemTypes
        .filter(type => (type.models ?? []).some(m => m.uuid === form.model_uuid))
        .map(type => type.uuid)
    );
  }, [modemTypes, form.model_uuid]);

  // Modems del desplegable: todos los libres compatibles con box y
  // si hay ya modelo seleccionado, solo los compatibles con este. 
  const selectableModems = useMemo(
    () => data.freeModems.filter(modem =>
      boxCapableTypes.has(modem.modem_type_uuid) &&
      (!typesForModel
        || typesForModel.has(modem.modem_type_uuid)
        || modem.imei === form.modem_imei)
    ),
    [data.freeModems, boxCapableTypes, typesForModel, form.modem_imei]
  );

  // Modelos del desplegable. Si hay un tipo de modem elegido, 
  // solo se muestran los modelos compatibles con ese tipo.
  const compatibleModels = useMemo(() => {
    const kiboxModels = models.filter(m => m.device_type === "kibox");
    if (!form.modem_type_uuid) return kiboxModels;

    const type = modemTypes.find(t => t.uuid === form.modem_type_uuid);
    if (!type) return EMPTY_ARRAY;

    const allowed = new Set(
      (type.models ?? []).filter(m => m.device_type === "kibox").map(m => m.uuid)
    );
    return kiboxModels.filter(m => allowed.has(m.uuid));
  }, [models, modemTypes, form.modem_type_uuid]);

  // ===== 4. FUNCIONES PRINCIPALES =====
  const Pfetch = useCallback(async () => {
    if (!ident) return;

    updateUI('loading', true);
    try {
      const res = await service.get("boards/" + ident);
      if (res.data.ok === false) {
        notification["error"]({
          message: lang.handle(res.data.message),
        });
        return;
      }

      updateData('raw', res.data);
    } catch (err) {
      console.log("Error devuelto: " + err);
    } finally {
      updateUI('loading', false);
    }
  }, [ident, lang]);

  const fetchFreeModems = useCallback(async () => {
    if (!newBoard) return;

    try {
      const res = await service.get("modems", { free: true });
      if (!res || res.data.ok === false) return;
      updateData('freeModems', res.data.data || []);
    } catch (err) {
      console.error("Error devuelto: " + err);
    }
  }, [newBoard]);

  const save = async (boardData) => {
    const urlListaKibox = "/boards/manufacture/list/1";

    try {
      if (newBoard) {
        const res = await service.post("boards", boardData);
        if (res.data.ok === false) {
          notification["error"]({
            message: lang.handle(res.data.message),
          });
        } else {
          notification["success"]({
            message: lang.handle('Placa creada correctamente'),
          });
          navigate(urlListaKibox);
        }
      } else {
        const res = await service.put("boards/" + ident, boardData);
        if (res.data.ok === false) {
          notification["error"]({
            message: lang.handle(res.data.message),
          });
        } else {
          notification["success"]({
            message: lang.handle('Placa actualizada correctamente'),
          });
          navigate(urlListaKibox);
        }
      }
    } catch (err) {
      console.log("Error: " + err);
    }
  };

  // ===== 5. HANDLERS =====
  const generateUUID = () => {
    updateForm('uuid', uuidv4());
  };

  const onInputChange = (e) => {
    const { name, value } = e.target;
    let errorMsg;

    switch (name) {
      case "uuid":
        errorMsg = uuidValidation(value) ? "" : '¡' + lang.handle('UUID erróneo') + '!';
        updateErrors('uuid', errorMsg);
        break;
      case "physical_id":
        errorMsg = macValidation(value) || imeiValidation(value) ? "" : '¡' + lang.handle('Introduzca una MAC o IMEI válidos') + '!';
        updateErrors('physical_id', errorMsg);
        break;
      default:
        break;
    }

    updateForm(name, value);
  };

  const onModemChange = (value) => {
    const imei = value ?? "";
    const modem = data.freeModems.find(item => item.imei === imei);
    const type = modemTypes.find(t => t.uuid === modem?.modem_type_uuid);

    const modelStillValid = !imei
      || !form.model_uuid
      || (type?.models ?? []).some(m => m.uuid === form.model_uuid);

    setForm(prev => ({
      ...prev,
      modem_imei: imei,
      modem_type_uuid: modem?.modem_type_uuid ?? "",
      physical_id: imei,
      model_uuid: modelStillValid ? prev.model_uuid : "",
      version_uuid: modelStillValid ? prev.version_uuid : "",
    }));

    if (!modelStillValid){
      updateData('filtered_version_list', []);
    } 
  };

  const onModelChange = (value) => {
    const filtered = versions.filter(version => version.model_uuid === value);
    const type = modemTypes.find(t => t.uuid === form.modem_type_uuid);

    const modemStillValid = !value
      || !form.modem_imei
      || (type?.models ?? []).some(m => m.uuid === value);

    setForm(prev => ({
      ...prev,
      model_uuid: value,
      version_uuid: "",
      modem_imei: modemStillValid ? prev.modem_imei : "",
      modem_type_uuid: modemStillValid ? prev.modem_type_uuid : "",
    }));
    updateData('filtered_version_list', filtered);
  };

  const onChangeDate = (date, dateString) => {
    updateForm('date', dateString);
  };

  const onSubmit = async (e) => {
    e.preventDefault();

    if (!errors.uuid && !errors.physical_id) {
      save(form);
    } else {
      if (errors.physical_id) {
        notification["error"]({
          message: lang.handle('Introduzca una MAC o IMEI válidos'),
        });
      }
      if (errors.uuid) {
        notification["error"]({
          message: lang.handle('UUID erróneo'),
        });
      }
    }
  };

  // ===== 6. EFFECTS =====
  useEffect(() => {
    if (ident) {
      Pfetch();
    }
  }, [ident, Pfetch]);

  useEffect(() => {
    fetchFreeModems();
  }, [fetchFreeModems]);

  useEffect(() => {
    const board = data.raw;
    if (!board) return;

    const version = versions.find(v => v.uuid === board.version_uuid);
    const model_uuid = version ? version.model_uuid : "";
    const filtered = model_uuid
      ? versions.filter(v => v.model_uuid === model_uuid)
      : [];

    setForm({
      ki_id: board.ki_id,
      uuid: board.uuid,
      hardware_version: board.hardware_version,
      physical_id: board.physical_id,
      date: dayjs(board.date).format('DD-MM-YYYY'),
      model_uuid: model_uuid,
      version_uuid: board.version_uuid,
      usb: board.usb,
      state: board.state,
      notes: board.notes,
      modem_imei: board.modem_imei ?? "",
      modem_type_uuid: board.modem_type_uuid ?? "",
      _id: board._id,
    });

    updateData('filtered_version_list', filtered);
  }, [data.raw, versions]);

  // Llegada desde Stock Teltonika: el modem ya viene elegido en la URL
  useEffect(() => {
    if (!newBoard) return;
    const imei = searchParams.get('modem_imei');
    if (!imei || data.freeModems.length === 0) return;

    const modem = data.freeModems.find(item => item.imei === imei);
    if (!modem) {
      notification["warning"]({ message: lang.handle('El modem ya no está disponible') });
      return;
    }

    setForm(prev => (prev.modem_imei === imei ? prev : {
      ...prev,
      modem_imei: modem.imei,
      modem_type_uuid: modem.modem_type_uuid,
      physical_id: modem.imei,
    }));
  }, [newBoard, searchParams, data.freeModems, lang]);

  // ===== 7. RENDER HELPERS =====
  const title = newBoard
    ? lang.handle('Nuevo kibox')
    : lang.handle('Información de kibox') + ": " + form.ki_id;

  const modemOptions = useMemo(
    () => selectableModems.map(modem => ({
      value: modem.imei,
      label: `${modem.imei} — ${modem.modem_type_code}`,
    })),
    [selectableModems]
  );

  const modelOptions = useMemo(
    () => compatibleModels.map(model => ({ value: model.uuid, label: model.name })),
    [compatibleModels]
  );

  const versionOptions = useMemo(
    () => data.filtered_version_list.map(version => ({ value: version.uuid, label: version.name })),
    [data.filtered_version_list]
  );

  // ===== 9. RENDER =====
  return (
    <div className="card">
      {ui.loading && <Loading loading={ui.loading} />}
      <h4 className="p-3"> {title} </h4>
      <form onSubmit={onSubmit} className="p-3">
        <div className="form-group row">
          <label className="col-sm-2 col-form-label newCard-form-text"> {lang.handle('Modem Teltonika')} </label>
          <div className="col-sm-10">
            {newBoard ? (
              <Select
                className="form-control"
                placeholder={lang.handle('Sin modem Teltonika')}
                value={form.modem_imei || undefined}
                onChange={onModemChange}
                options={modemOptions}
                disabled={!editable}
                showSearch
                allowClear
                filterOption={(input, option) =>
                  option.label.toLowerCase().indexOf(input.toLowerCase()) >= 0
                }
              />
            ) : (
              <Input
                type="text"
                className="form-control"
                value={form.modem_imei ? `${form.modem_imei} — ${data.raw?.modem_type_code ?? ''}` : ''}
                disabled
              />
            )}
          </div>
        </div>
        <div className="form-group row">
          <label
            htmlFor="uuid"
            className="col-sm-2 col-form-label newCard-form-text"
          >
            UUID
          </label>
          <div className="col-sm-10">
            <Input
              type="text"
              className="form-control"
              id="uuid"
              placeholder="37555f8a-1cab-486b-ab2e-1c65e80b2c12"
              onChange={onInputChange}
              name="uuid"
              value={form.uuid}
              disabled={!editable}
              required
              suffix={
                <Tooltip title="Generar UUID">
                  <ReloadOutlined
                    onClick={generateUUID}
                    style={{ cursor: 'pointer' }}
                  />
                </Tooltip>
              }
            />
            {errors.uuid.length > 0 && (
              <span className="boardFormError">{errors.uuid}</span>
            )}
          </div>
        </div>

        <div className="form-group row">
          <label
            htmlFor="physical_id"
            className="col-sm-2 col-form-label newCard-form-text"
          >
            MAC / IMEI
          </label>
          <div className="col-sm-10">
            <Input
              type="text"
              className="form-control"
              id="physical_id"
              placeholder="30:A4:F5:20:4C:5A / 524317018414818"
              onChange={onInputChange}
              name="physical_id"
              value={form.physical_id}
              disabled={!editable || !!form.modem_imei}
              required
            />
            {errors.physical_id.length > 0 && (
              <span className="boardFormError">{errors.physical_id}</span>
            )}
          </div>
        </div>

        <div className="form-group row">
          <div className="col newCard-form-text"> {lang.handle('Fecha fabricación')} </div>
          <div className="col-sm-10">
            <DatePicker
              className="form-control"
              value={dayjs(form.date, "DD-MM-YYYY")}
              onChange={onChangeDate}
              format="DD-MM-YYYY"
              disabled={!editable}
              allowClear={false}
              required
            />
          </div>
        </div>

        <div className="form-group row">
          <div className="col newCard-form-text"> {lang.handle('Modelo placa')} </div>
          <div className="col-sm-10">
            <Select
              className="form-control"
              placeholder={lang.handle('Seleccionar opción')}
              value={form.model_uuid || undefined}
              onChange={onModelChange}
              options={modelOptions}
              disabled={!editable}
              allowClear={true}
            />
          </div>
        </div>

        <div className="form-group row">
          <div className="col newCard-form-text"> {lang.handle('Versión placa')} </div>
          <div className="col-sm-10">
            <Select
              className="form-control"
              placeholder={form.model_uuid
                ? lang.handle('Seleccionar opción')
                : lang.handle('Primero seleccione un modelo')}
              value={form.version_uuid || undefined}
              onChange={(value) => updateForm('version_uuid', value)}
              options={versionOptions}
              disabled={!editable || !form.model_uuid}
            />
          </div>
        </div>

        <div className="form-group row">
          <label
            htmlFor="notes"
            className="col-sm-2 col-form-label newCard-form-text"
          >
            {lang.handle('Notas')}
          </label>
          <div className="col-sm-10">
            <Input
              type="text"
              className="form-control"
              id="notes"
              placeholder={lang.handle('Tarda demasiado en encenderse')}
              onChange={onInputChange}
              name="notes"
              value={form.notes}
              disabled={!editable}
            />
          </div>
        </div>

        {editable && (
          <button
            id="button-save"
            className="btn btn-primary d-flex float-right"
            type="submit"
          >
            <div className="btnText">
              <p> {lang.handle('Guardar')}&nbsp;&nbsp; </p>
              <i className="material-icons"> save </i>
            </div>
          </button>
        )}
      </form>
    </div>
  );
};

export default withLocale(withNavigation(ManufactureBoard));
